/**
 * Admin Layout
 *
 * Admin-specific layout with dark theme and admin navigation.
 * Blueprint Task: 2.3.3
 */

import { useState } from 'react';
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';

import { PORTAL_BRAND } from '../config/branding';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../providers/AuthProvider';

const navigation = [
  { name: 'Dashboard', href: '/admin', icon: DashboardIcon },
  { name: 'Customers', href: '/admin/customers', icon: UsersIcon },
  { name: 'Services', href: '/admin/services', icon: CubeIcon },
  { name: 'Users', href: '/admin/users', icon: ShieldIcon },
  { name: 'Documents', href: '/admin/documents', icon: DocumentIcon },
  { name: 'Fulfillment', href: '/admin/fulfillment', icon: ClipboardIcon },
  { name: 'SOS Requests', href: '/admin/sos', icon: AlertIcon },
  { name: 'RAC Pipeline', href: '/admin/rac', icon: RACIcon },
  { name: 'EIN Applications', href: '/admin/ein', icon: EINIcon },
  { name: 'Audit Log', href: '/admin/audit-log', icon: AuditLogIcon },
  { name: 'Control Room', href: '/admin/control-room', icon: CommandIcon, highlight: true },
];

/**
 *
 */
export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();

  // Generate breadcrumbs from path
  const breadcrumbs = location.pathname
    .split('/')
    .filter(Boolean)
    .map((segment, index, array) => ({
      name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
      href: '/' + array.slice(0, index + 1).join('/'),
      current: index === array.length - 1,
    }));

  const userRole = user?.role ?? 'admin';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--admin-bg))' }}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/70 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: 'hsl(var(--admin-surface))' }}
      >
        <div
          className="flex h-16 items-center justify-between border-b px-4"
          style={{ borderColor: 'hsl(var(--admin-border))' }}
        >
          <span className="text-xl font-bold" style={{ color: 'hsl(var(--color-error-400))' }}>
            {PORTAL_BRAND.id === 'eleet' ? 'Admin Portal' : `${PORTAL_BRAND.shortName} Admin`}
          </span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="rounded-md p-2"
            style={{ color: 'hsl(var(--color-neutral-400))' }}
          >
            <XIcon className="h-6 w-6" />
          </button>
        </div>
        <nav className="mt-4 px-2">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/admin'}
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium"
              style={({ isActive }) => ({
                backgroundColor: isActive ? 'hsl(var(--admin-hover))' : undefined,
                color: isActive
                  ? 'hsl(var(--color-neutral-900))'
                  : item.highlight
                    ? 'hsl(var(--color-primary-400))'
                    : 'hsl(var(--color-neutral-400))',
              })}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div
          className="flex min-h-0 flex-1 flex-col border-r"
          style={{
            backgroundColor: 'hsl(var(--admin-surface))',
            borderColor: 'hsl(var(--admin-border))',
          }}
        >
          <div
            className="flex h-16 items-center border-b px-6"
            style={{ borderColor: 'hsl(var(--admin-border))' }}
          >
            <Link to="/admin" className="flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded"
                style={{ backgroundColor: 'hsl(var(--color-error-600))' }}
              >
                {PORTAL_BRAND.id === 'eleet' ? <CommandIcon className="h-5 w-5 text-white" /> : <img src={PORTAL_BRAND.logoMarkUrl} alt="" className="h-8 w-8 rounded" />}
              </div>
              <span className="text-lg font-bold" style={{ color: 'hsl(var(--color-neutral-900))' }}>
                {PORTAL_BRAND.id === 'eleet' ? 'Admin' : `${PORTAL_BRAND.shortName} Admin`}
              </span>
            </Link>
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/admin'}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors"
                style={({ isActive }) => ({
                  backgroundColor: isActive ? 'hsl(var(--admin-hover))' : undefined,
                  color: isActive
                    ? 'hsl(var(--color-neutral-900))'
                    : item.highlight
                      ? 'hsl(var(--color-primary-400))'
                      : 'hsl(var(--color-neutral-400))',
                })}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>
          <div className="border-t p-4" style={{ borderColor: 'hsl(var(--admin-border))' }}>
            <Link
              to="/dashboard"
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm"
              style={{ color: 'hsl(var(--color-neutral-400))' }}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Portal
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header
          className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b px-4 lg:px-6"
          style={{
            backgroundColor: 'hsl(var(--admin-surface))',
            borderColor: 'hsl(var(--admin-border))',
          }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-md p-2 lg:hidden"
            style={{ color: 'hsl(var(--color-neutral-400))' }}
          >
            <MenuIcon className="h-6 w-6" />
          </button>

          {/* Breadcrumbs */}
          <nav className="hidden items-center gap-2 text-sm sm:flex">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.href} className="flex items-center gap-2">
                {index > 0 && (
                  <ChevronRightIcon
                    className="h-4 w-4"
                    style={{ color: 'hsl(var(--color-neutral-600))' }}
                  />
                )}
                {crumb.current ? (
                  <span style={{ color: 'hsl(var(--color-neutral-900))' }}>{crumb.name}</span>
                ) : (
                  <Link
                    to={crumb.href}
                    style={{ color: 'hsl(var(--color-neutral-400))' }}
                    className="hover:underline"
                  >
                    {crumb.name}
                  </Link>
                )}
              </span>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-4">
            {/* Theme toggle */}
            <button
              onClick={() => setTheme(theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light')}
              className="rounded-md p-2 transition-colors"
              style={{ color: 'hsl(var(--color-neutral-400))' }}
              title={`Theme: ${theme}`}
            >
              {theme === 'light' ? (
                <SunIcon className="h-5 w-5" />
              ) : theme === 'dark' ? (
                <MoonIcon className="h-5 w-5" />
              ) : (
                <MonitorIcon className="h-5 w-5" />
              )}
            </button>

            {/* Role badge */}
            <span
              className="rounded-full px-3 py-1 text-xs font-medium uppercase"
              style={{
                backgroundColor: 'hsl(var(--color-error-900))',
                color: 'hsl(var(--color-error-300))',
              }}
            >
              {userRole}
            </span>

            {/* User menu */}
            <div className="flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium"
                style={{
                  backgroundColor: 'hsl(var(--color-error-600))',
                  color: 'white',
                }}
              >
                {user?.name?.charAt(0).toUpperCase() ?? user?.email?.charAt(0).toUpperCase()}
              </div>
              <span
                className="hidden text-sm font-medium sm:block"
                style={{ color: 'hsl(var(--color-neutral-800))' }}
              >
                {user?.name ?? user?.email}
              </span>
            </div>
            <button
              onClick={logout}
              className="rounded-md px-3 py-1.5 text-sm font-medium"
              style={{
                color: 'hsl(var(--color-neutral-400))',
                backgroundColor: 'hsl(var(--admin-hover))',
              }}
            >
              Sign out
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>

        {/* Legacy legal footer; the company footer is global on Geaux File. */}
        {PORTAL_BRAND.id === 'eleet' ? <footer
          className="border-t px-4 py-3 text-center text-xs"
          style={{
            borderColor: 'hsl(var(--admin-border))',
            color: 'hsl(var(--color-neutral-500))',
          }}
        >
          <a href={PORTAL_BRAND.termsUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">Terms</a>
          {' | '}
          <a href={PORTAL_BRAND.privacyUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy</a>
          {' | '}
          <a href={PORTAL_BRAND.acceptableUseUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">Acceptable Use</a>
        </footer> : null}
      </div>
    </div>
  );
}

// Icon components
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function CubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function CommandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
    </svg>
  );
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
    </svg>
  );
}

function ChevronRightIcon({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function AuditLogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5a2.25 2.25 0 002.25 2.25h6.75A2.25 2.25 0 0019.5 19.5V6.108c0-1.135-.845-2.098-1.976-2.192a48.507 48.507 0 00-1.123-.08" />
    </svg>
  );
}

function RACIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function EINIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
    </svg>
  );
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}
